/* Service Worker registration helper for the XYX Protocol PWA.
 *
 * Only registers in production builds — running a service worker against a
 * webpack-dev-server hot-reload setup causes cached-stale-bundle headaches
 * during development.
 *
 * Also handles new-version detection: when a fresh service worker becomes
 * available (deploy day), we dispatch a `vault:sw-update-ready` event the
 * app can listen for to show an "App update available" toast.
 */
export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Don't register on localhost dev OR when running inside a Capacitor
  // native shell (the shell already controls navigation).
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    /^192\.168\./.test(window.location.hostname);
  const isCapacitor = !!window.Capacitor;
  if (isLocalhost || isCapacitor) return;

  window.addEventListener("load", async () => {
    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js", {
        scope: "/",
      });

      // When a new version is found, notify the app so it can show a toast.
      reg.addEventListener("updatefound", () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            try {
              // Tell the new SW to take over immediately — no need to wait
              // for every tab to close, which never happens for installed
              // PWAs and was the root cause of users seeing stale bundles
              // after a deploy.
              sw.postMessage("SKIP_WAITING");
              window.dispatchEvent(new CustomEvent("vault:sw-update-ready"));
            } catch { /* ignore */ }
          }
        });
      });

      // When the active service worker changes (i.e. the new SW just took
      // over via skipWaiting), force a one-time reload so the page picks up
      // the freshest JS bundles. Guarded against reload loops with sessionStorage.
      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded) return;
        try {
          if (sessionStorage.getItem("vault.sw.reloaded") === "1") return;
          sessionStorage.setItem("vault.sw.reloaded", "1");
        } catch { /* ignore */ }
        reloaded = true;
        window.location.reload();
      });

      // Belt + suspenders: actively check for a new SW on every load.
      try { reg.update(); } catch { /* ignore */ }
    } catch (e) {
      // Silent — service worker is a progressive enhancement.
      console.warn("[sw] registration failed:", e);
    }
  });

  // Listen for postMessage from the SW (we don't use this yet but hook for later).
  navigator.serviceWorker.addEventListener("message", () => { /* hook */ });
}

export function unregisterServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
}
